import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { handleAdminDashboard } from "../../services/APIClient";
import { useLoader } from "../../contexts/LoaderContext";

const hasValue = (value) => value !== null && value !== undefined && Number.isFinite(Number(value));

const money = (value) =>
  hasValue(value)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(value))
    : "—";

const number = (value) =>
  hasValue(value)
    ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(Number(value))
    : "—";

const percent = (value) => (hasValue(value) ? `${number(value)}%` : "—");

const STATE_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

const MetricCard = ({ label, value, detail, href, accent = false, tone = "default" }) => {
  const tones = {
    default: "border-white/10 bg-white/[0.045] hover:border-white/20 hover:bg-white/[0.07]",
    blue: "border-[#67A3FA]/50 bg-gradient-to-br from-[#172033] to-[#0B0D12] shadow-[0_18px_55px_rgba(103,163,250,0.14)]",
    amber: "border-amber-300/25 bg-amber-300/[0.055]",
  };
  const content = (
    <div className={`h-full rounded-2xl border p-5 transition-all duration-200 sm:p-6 ${accent ? tones.blue : tones[tone]}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/45">{label}</p>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">{value}</p>
      {detail && <p className="mt-2 text-sm leading-5 text-white/50">{detail}</p>}
    </div>
  );
  return href ? <Link className="block h-full" to={href}>{content}</Link> : content;
};

const Section = ({ eyebrow, title, description, children, action }) => (
  <section className="rounded-3xl border border-white/10 bg-[#0D1017] p-5 sm:p-7">
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#67A3FA]">{eyebrow}</p>
        <h2 className="mt-2 text-xl font-semibold text-white sm:text-2xl">{title}</h2>
        {description && <p className="mt-2 max-w-2xl text-sm leading-6 text-white/45">{description}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const ProgressRow = ({ label, value, total, note }) => {
  const safeValue = hasValue(value) ? Number(value) : 0;
  const safeTotal = hasValue(total) ? Number(total) : 0;
  const width = safeTotal > 0 ? Math.min((safeValue / safeTotal) * 100, 100) : 0;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-4 text-sm">
        <div>
          <span className="font-medium text-white/80">{label}</span>
          {note && <span className="ml-2 text-white/35">{note}</span>}
        </div>
        <span className="font-semibold text-white">{number(value)}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
        <div className="h-full rounded-full bg-gradient-to-r from-[#67A3FA] to-[#9CC7FF]" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

const FunnelStep = ({ label, value, baseline, detail }) => {
  const width = hasValue(value) && baseline > 0 ? Math.max(Math.min((Number(value) / baseline) * 100, 100), 2) : 0;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium text-white/80">{label}</p>
          <p className="mt-1 text-xs leading-5 text-white/40">{detail}</p>
        </div>
        <p className="text-2xl font-semibold text-white">{number(value)}</p>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
        <div className="h-full rounded-full bg-gradient-to-r from-[#2B69C9] to-[#79B2FF]" style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

const AttentionItem = ({ label, value, detail, href }) => (
  <Link className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.035] p-4 transition hover:border-amber-200/30 hover:bg-amber-200/[0.045]" to={href}>
    <div>
      <p className="text-sm font-medium text-white/80">{label}</p>
      <p className="mt-1 text-xs leading-5 text-white/40">{detail}</p>
    </div>
    <div className="flex items-center gap-3">
      <span className="text-2xl font-semibold text-amber-100">{number(value)}</span>
      <span className="text-white/25 transition group-hover:text-white/65">→</span>
    </div>
  </Link>
);

const ActivityChart = ({ activity = [] }) => {
  const recent = activity.slice(-14);
  const max = Math.max(1, ...recent.map((day) => (day.newUsers || 0) + (day.savedSummonses || 0) + (day.overtimeRecords || 0)));

  if (!recent.length) {
    return <div className="flex h-44 items-center justify-center rounded-2xl border border-dashed border-white/10 text-sm text-white/35">Activity data is not available yet.</div>;
  }

  return (
    <div>
      <div className="flex h-44 items-end gap-2 sm:gap-3">
        {recent.map((day) => {
          const total = (day.newUsers || 0) + (day.savedSummonses || 0) + (day.overtimeRecords || 0);
          return (
            <div className="group flex min-w-0 flex-1 flex-col items-center justify-end" key={day.date}>
              <div className="pointer-events-none mb-2 hidden whitespace-nowrap rounded-lg border border-white/10 bg-black px-2 py-1 text-[11px] text-white/80 group-hover:block">{total} actions</div>
              <div className="w-full min-w-[5px] rounded-t-md bg-gradient-to-t from-[#2B69C9] to-[#79B2FF] transition-all group-hover:brightness-125" style={{ height: `${Math.max((total / max) * 128, 4)}px` }} />
              <span className="mt-2 hidden text-[10px] text-white/30 sm:block">{day.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/45">
        <span>Registrations + saved summonses + overtime records</span>
        <span>Last 14 days</span>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [dashboard, setDashboardData] = useState({});
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  const fetchData = useCallback(async ({ initial = false } = {}) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    try {
      const dashboardRes = await handleAdminDashboard();
      const payload = dashboardRes?.data?.response ?? dashboardRes?.data ?? dashboardRes?.response ?? dashboardRes ?? {};
      if (!payload?.summary && payload?.totalUser == null) throw new Error("Dashboard metrics were not returned");
      setDashboardData(payload);
      setError("");
    } catch (err) {
      setError("The Command Center could not refresh. Existing numbers may be out of date.");
    } finally {
      if (initial) setLoading(false);
      else setRefreshing(false);
    }
  }, [setLoading]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchData({ initial: true });
  }, [fetchData]);

  const summary = dashboard?.summary || {};
  const growth = dashboard?.growth || {};
  const funnel = dashboard?.funnel || {};
  const retention = dashboard?.retention || {};
  const subscriptions = dashboard?.subscriptions || {};
  const product = dashboard?.product || {};
  const content = dashboard?.content || {};
  const geography = dashboard?.geography || {};
  const activity = dashboard?.trends?.activity || [];
  const totalPaid = summary.activePaidSubscribers || 0;
  const registeredUsers = summary.registeredUsers ?? funnel.registeredUsers ?? summary.totalUsers ?? dashboard.totalUser;
  const activeAccounts = summary.activeAccounts ?? summary.totalUsers;
  const inactiveAccounts = hasValue(registeredUsers) && hasValue(activeAccounts) ? Math.max(Number(registeredUsers) - Number(activeAccounts), 0) : null;
  const unactivatedNewUsers = hasValue(growth.newUsers30d) && hasValue(retention.newUsersActivated30d)
    ? Math.max(Number(growth.newUsers30d) - Number(retention.newUsersActivated30d), 0)
    : null;
  const baseline = Number(funnel.registeredUsers || registeredUsers || 0);

  const updatedLabel = useMemo(() => {
    if (!dashboard?.updatedAt) return "Not refreshed yet";
    return `Updated ${new Date(dashboard.updatedAt).toLocaleString()}`;
  }, [dashboard?.updatedAt]);

  return (
    <div className="min-h-screen bg-[#07090D] text-white">
      <div className="mx-auto w-full max-w-[1600px] px-4 py-7 sm:px-6 lg:px-8 lg:py-10">
        <header className="mb-8 flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#67A3FA]">NinetySix Command Center</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">Company health at a glance</h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-white/50 sm:text-base">Revenue momentum, officer activation, recurring usage and items that need attention.</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs text-white/45">{updatedLabel}</div>
            <button className="rounded-full border border-white/15 bg-white/[0.055] px-4 py-2 text-xs font-semibold text-white/75 transition hover:border-[#67A3FA]/50 hover:text-white disabled:cursor-wait disabled:opacity-50" disabled={refreshing} onClick={() => fetchData()} type="button">
              {refreshing ? "Refreshing…" : "Refresh data"}
            </button>
          </div>
        </header>

        {error && <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-400/30 bg-red-400/10 px-5 py-4 text-sm text-red-100"><span>{error}</span><button className="font-semibold underline decoration-red-200/40 underline-offset-4" onClick={() => fetchData()} type="button">Try again</button></div>}

        <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <MetricCard accent detail="Recurring plan estimate—not settled revenue" label="Estimated MRR" value={money(summary.estimatedMRR)} />
          <MetricCard accent detail="Active recurring subscriptions" href="/subscription" label="Paid subscribers" value={number(summary.activePaidSubscribers)} />
          <MetricCard detail="New officers who saved a draft" label="30-day activation" value={percent(retention.newUserActivationRate30d)} />
          <MetricCard detail="Creators with two or more saved drafts" label="Repeat creator rate" value={percent(retention.repeatCreatorRate)} />
          <MetricCard detail={`${number(growth.newUsers30d)} joined in 30 days`} href="/officers" label="Registered officers" value={number(registeredUsers)} />
        </div>

        <div className="grid gap-7 xl:grid-cols-[1.15fr_0.85fr]">
          <Section eyebrow="Conversion" title="Officer journey" description="The three outcomes that matter: officers register, experience the testimony workflow and choose a recurring plan.">
            <div className="space-y-3">
              <FunnelStep baseline={baseline} detail="All non-deleted accounts" label="Registered" value={funnel.registeredUsers ?? registeredUsers} />
              <FunnelStep baseline={baseline} detail={`${percent(funnel.activationRate)} of registered officers`} label="Saved a testimony draft" value={funnel.savedSummonsCreators} />
              <FunnelStep baseline={baseline} detail={`${percent(funnel.paidConversionRate ?? summary.paidConversionRate)} of registered officers`} label="Active paid subscription" value={funnel.activePaidSubscribers ?? summary.activePaidSubscribers} />
            </div>
          </Section>

          <Section eyebrow="Needs attention" title="Action queue" description="Operational signals worth reviewing today—not vanity metrics.">
            <div className="space-y-3">
              <AttentionItem detail="Recurring plans marked to stop" href="/subscription" label="Cancellation scheduled" value={subscriptions.scheduledCancellations} />
              <AttentionItem detail="Subscriptions ending within seven days" href="/subscription" label="Ending soon" value={subscriptions.expiringNext7Days} />
              <AttentionItem detail="Joined in 30 days without a saved draft" href="/officers?attention=unactivated30d" label="New officers not activated" value={unactivatedNewUsers} />
              <AttentionItem detail="Registered accounts currently inactive" href="/officers?attention=inactive" label="Inactive accounts" value={inactiveAccounts} />
            </div>
          </Section>
        </div>

        <div className="mt-7 grid gap-7 xl:grid-cols-[1.35fr_0.65fr]">
          <Section eyebrow="Momentum" title="30-day growth and activity">
            <ActivityChart activity={activity} />
            <div className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[["New today", growth.newUsersToday], ["New 7 days", growth.newUsers7d], ["New 30 days", growth.newUsers30d], ["Active 7 days", growth.activeUsers7d], ["Active 30 days", growth.activeUsers30d]].map(([label, value]) => (
                <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4" key={label}><p className="text-xl font-semibold text-white">{number(value)}</p><p className="mt-1 text-xs text-white/40">{label}</p></div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Subscription health" title="Recurring customers" action={<button className="text-sm font-medium text-[#7DB4FF] hover:text-white" onClick={() => navigate("/subscription")} type="button">View subscriptions →</button>}>
            <div className="space-y-6">
              <ProgressRow label="Yearly" total={totalPaid} value={subscriptions.yearlySubscribers} />
              <ProgressRow label="Monthly" total={totalPaid} value={subscriptions.monthlySubscribers} />
              <ProgressRow label="Web" total={totalPaid} value={subscriptions.webSubscribers} />
              <ProgressRow label="iOS" total={totalPaid} value={subscriptions.iosSubscribers} />
            </div>
            <div className="mt-7 grid grid-cols-2 gap-3">
              {[["Active trials", subscriptions.activeTrials], ["Trial users all time", subscriptions.trialEverUsed]].map(([label, value]) => (
                <div className="rounded-xl border border-white/10 bg-white/[0.035] p-4" key={label}><p className="text-2xl font-semibold text-white">{number(value)}</p><p className="mt-1 text-xs text-white/40">{label}</p></div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-7 grid gap-7 lg:grid-cols-2">
          <Section eyebrow="Retention" title="Recurring product value" description="Behavior that indicates officers are returning to NinetySix—not merely creating accounts.">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[["Creators in 30 days", retention.savedSummonsCreators30d], ["Repeat creators", retention.repeatCreators], ["Returned in 30 days", retention.returningCreators30d], ["Saved summonses", product.savedSummonses], ["Saved in 30 days", product.savedSummonses30d], ["ChatGPT saves", product.chatgptSavedSummonses]].map(([label, value]) => (
                <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5" key={label}><p className="text-2xl font-semibold text-white sm:text-3xl">{number(value)}</p><p className="mt-2 text-xs leading-5 text-white/40">{label}</p></div>
              ))}
            </div>
          </Section>

          <Section eyebrow="Product and reach" title="Supporting activity">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {[["Testimonies generated", product.totalTestimonies ?? dashboard.totalTestimony], ["Overtime records", product.overtimeRecords], ["Overtime in 30 days", product.overtimeRecords30d], ["Published blogs", content.publishedBlogs], ["Blog views", content.totalBlogViews], ["Email reach", content.broadcastReach]].map(([label, value]) => (
                <div className="rounded-2xl border border-white/10 bg-white/[0.035] p-4 sm:p-5" key={label}><p className="text-2xl font-semibold text-white sm:text-3xl">{number(value)}</p><p className="mt-2 text-xs leading-5 text-white/40">{label}</p></div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-7">
          <Section eyebrow="Service footprint" title="Where officers use NinetySix" description="Optional state or territory data shown only in aggregate. NinetySix does not collect precise location for this report.">
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <MetricCard detail="Officers who voluntarily selected a state" label="Coverage known" value={number(geography.knownLocations)} />
              <MetricCard detail="Officers who did not provide a state" label="Not provided" value={number(geography.unknownLocations)} />
              <MetricCard detail="States and territories represented" label="Markets reached" value={number(geography.states?.length)} />
            </div>
            {geography.states?.length ? (
              <div className="grid gap-x-8 gap-y-5 md:grid-cols-2">
                {geography.states.slice(0, 12).map((item) => (
                  <ProgressRow
                    key={item.stateCode}
                    label={STATE_NAMES.of(item.stateCode) || item.stateCode}
                    note={item.stateCode}
                    total={geography.knownLocations}
                    value={item.count}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-white/35">
                State coverage will appear as officers voluntarily update their profiles.
              </div>
            )}
          </Section>
        </div>

        <div className="mt-7">
          <Section eyebrow="Definitions" title="How these numbers are calculated" description="These definitions keep the Command Center honest and prevent estimated values from being confused with accounting records." action={null}>
            <div className="grid gap-3 text-xs leading-5 text-white/45 md:grid-cols-2 xl:grid-cols-4">
              <p className="rounded-xl border border-white/10 bg-white/[0.025] p-4"><strong className="block text-white/75">Estimated MRR</strong>Active recurring plan prices normalized monthly. Excludes taxes, refunds, processor fees and one-time purchases.</p>
              <p className="rounded-xl border border-white/10 bg-white/[0.025] p-4"><strong className="block text-white/75">Activated officer</strong>Registered officer who has saved at least one testimony draft.</p>
              <p className="rounded-xl border border-white/10 bg-white/[0.025] p-4"><strong className="block text-white/75">Repeat creator</strong>Officer who has saved two or more testimony drafts.</p>
              <p className="rounded-xl border border-white/10 bg-white/[0.025] p-4"><strong className="block text-white/75">Active officer</strong>Active account with a recorded login during the selected period.</p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
